// Extended by FormComponent
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import FormComponent from '../FormComponent';
import Request, { ApiMode } from '../../utils/request';
import { InputTypes, validFormElementValue } from '../../utils/validateFormElement';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';
import { Events } from '../../utils/dom/html-events';

const IS_LOCAL = false;

class RadioComponent extends FormComponent {
	constructor(element, config) {
		super(element, config);
		this.init();
	}
	init() {
		const valueField = this.data?.valueField || 'label';
		// if the value is already set, then update the value
		if (this.data?.value) {
			// check if the value is in the options
			const foundOption = this.data?.options?.find((option) => option[valueField] === this.data?.value);
			if (foundOption) {
				// if the value is in the options, then update the value
				this.value = { ...foundOption, fieldName: this.data.fieldName, value: foundOption[valueField] };
				// update the data to rerender the component
				this.updateData({});
			} else {
				// if the value is not in the options, then check if the value is in the others field
				const findOthersField = this.data?.options?.find((option) => option.isOthers);
				if (findOthersField) {
					// if the value is in the others field, then update the value
					this.value = { ...findOthersField, fieldName: this.data.fieldName, value: this.data?.value };
					// update the data to rerender the component
					this.updateData({});
				}
			}
		}
	}

	bindInstructionTooltip() {
		this.trigger = SelectorEngine.findOne('.js-lta-tooltip__trigger', this.containerEl);
		if (!this.trigger) {
			return;
		}

		EventHandler.on(this.trigger, 'click', (event_) => {
			this.clickHandler(event_);
		});
		EventHandler.on(this.trigger, 'keypress', (event_) => {
			if (event_.keyCode == 13) this.clickHandler(event_);
		});
	}

	clickHandler() {
		const content = this.trigger.nextElementSibling;
		content.classList.toggle('is-visible');
	}
	markup(value, data, titleMarkup) {
		this.radioContainerClass = `lta-radio-container-${data.id}`;
		const inputID = `lta-radio-button-id-${data.id}`;
		const disableLabel = this.data.allowReset ? this.renderDisableTitle() : '';
		const radioButtons = data.options.map((radioButton) => this.renderRadioButtons(radioButton, data)).join('');
		const labelData = data?.instructionText
			? `<label for=${inputID} class="lta-text-input__label u-text-grey-lta">${data?.instructionText}</label>`
			: '';
		const subtitleData = data?.subtitle ? `<p class="u-text-grey-dark-slate u-body2">${data?.subtitle}</p>` : '';
		const labelHtmlInfo = data?.instructionTooltip
			? `<div class="u-caption2 js-lta-tooltip sm:u-mb-2 md:u-mb-0 u-pr-1">
				<div class="sm:u-flex md:u-flex xl:u-hstack js-lta-tooltip__trigger lta-cookies-modal-title2 u-align-center u-mt-2" tabindex="0">
					<svg class="fe-icon u-mr-1" width="1.5em" height="1.5em" fill="currentColor" style="flex-shrink:0;">
						<use xlink:href="/static/images/icons/lta-icons.svg#info"></use>
					</svg>${data?.instructionTooltipTitle}
				</div>
				<div class="js-lta-tooltip__content lta-cookies-modal-text u-body1 u-pt-2 u-pl-3">
					<div class="u-body2">${data?.instructionTooltipDescription1}</div>
					<div class="u-body2">
						<a href=${data?.instructionTooltipDescription2Link} target="_blank">${data?.instructionTooltipDescription2}</a>
					</div>
				</div>
			</div>
			`
			: '';
		return `<div class="lta-radio-button">
				<div class="lta-radio-button-title">${titleMarkup}</div>
				<div class="${this.radioContainerClass} lta-form-check-group lta-content--fields lta-heading u-mt-3 u-mb-5">
					${labelData}
					<div class="lta-text-input--error"></div>
					${subtitleData}
					${labelHtmlInfo}
					${radioButtons}
					${disableLabel}
				</div>
			<div>`;
	}
	renderDisableTitle() {
		return `<div class="lta-radio-button--disable-button-wrapper u-flex">
					<button type="button" class="lta-radio-button--search-form-button lta-btn lta-btn--lg lta-btn--link">
						Role changed? Update it here
					</button>
				</div>`;
	}
	renderRadioButtons(radioButton, data) {
		const radioButtonName = `radio-button-name-question${this.data.id}`;
		const radioButtonID = `radio-button-question-${this.data.id}-${radioButton.id}`;
		const subHeading = radioButton?.subHeading ? `<p class="u-caption1 u-mb-1">${radioButton?.subHeading}</p>` : '';
		const valueField = data?.valueField || 'label';
		const isChecked = radioButton?.[valueField] === this?.value?.[valueField];
		const description = radioButton?.description || '';
		return `
				${subHeading}
				<label class="lta-form-lozenge-container u-body1 u-text-lta-dark-blue u-mb-1" for=${radioButtonID}>
					<div class="lta-form-lozenge">
						<input 
							class="lta-form-lozenge-input" 
							role="radio" 
							type="radio" 
							name=${radioButtonName}
							id=${radioButtonID}
							value=${radioButton.id}
							${isChecked ? 'checked' : ''} 
							${this.data.disable && `disabled="${this.data.disable}"`}/>
						<span class="lta-form-lozenge-border"></span>
						${
							description
								? `<div class='u-flex u-mb-1' style="flex-direction:column; gap:4px;">
								<span class="lta-form-lozenge-label u-body1"> ${radioButton.label} </span>
								<span class="lta-form-lozenge-label u-body2">${radioButton.description}</span>
							</div>							`
								: `<span class="lta-form-lozenge-label u-body1"> ${radioButton.label} </span>`
						}
					</div>
					${
						radioButton?.isOthers
							? `<div class="lta-form-lozenge-others">
						<input type="text" class="lta-text-input ${
							isChecked ? '' : 'u-hidden'
						} u-p-1 u-mt-2 lta-radio-button-input-others" placeholder="${
									radioButton?.othersPlaceholder ?? ''
							  }" value="${isChecked ? this.value.value : ''}">
					</div>`
							: ''
					}
				</label>`;
	}
	afterRender() {
		this.radioButtonContainerElement = SelectorEngine.findOne(`.${this.radioContainerClass}`, this.containerEl);
		this.radioButtonElements = SelectorEngine.find('input[type=radio]', this.radioButtonContainerElement);
		this.redirectButton = SelectorEngine.findOne(
			'.lta-radio-button--search-form-button',
			this.radioButtonContainerElement
		);
		this.selectedRadio = this.radioButtonElements.find((radio) => radio.checked);
		for (const radioButton of this.radioButtonElements) {
			EventHandler.on(radioButton, Events.Click, this.radioButtonClickHandler.bind(this));
		}

		if (this.redirectButton) {
			EventHandler.on(this.redirectButton, Events.Click, () => {
				this.redirectButtonClickHandler();
			});
		}
		const otherInputs = SelectorEngine.find('.lta-radio-button-input-others', this.radioButtonContainerElement);
		if (otherInputs.length > 0) {
			for (const input of otherInputs) {
				EventHandler.on(input, 'change', () => {
					this.value = {
						...this.value,
						fieldName: this?.data.fieldName,
						value: input.value
					};
				});
			}
		}
		this.bindInstructionTooltip();

		setTimeout(() => {
			if (this.selectedRadio) {
				this.selectedRadio.click();
			}
		}, 100);
	}
	async redirectButtonClickHandler() {
		startLoadingAnimation(true);
		try {
			const url = `/api/account/deleteschoolonboarding`;
			const response = await Request.get(
				url,
				{},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const { success, redirectUrl, error } = response;
			if (success) {
				this.goToUrl(redirectUrl);
			} else {
				stopLoadingAnimation();
			}

			if (!!error?.hasError) {
				stopLoadingAnimation();
				throw error;
			}
		} catch (error) {
			stopLoadingAnimation();
			if (error.hasError) {
				// eslint-disable-next-line no-console
				console.log('Error :', error);
			}
		}
	}
	hideNotSelectedOthersInput() {
		const otherInputs = SelectorEngine.find('.lta-radio-button-input-others:not(.u-hidden)', this._element);
		if (otherInputs.length > 0) {
			for (const input of otherInputs) {
				input.classList.add('u-hidden');
			}
		}
	}
	showSelectedOthersInput() {
		const otherInput = SelectorEngine.findOne(
			'.lta-radio-button-input-others',
			this.selectedRadio?.parentElement?.parentElement
		);
		if (otherInput) {
			otherInput.classList.remove('u-hidden');
			otherInput.focus();
		}
	}
	goToUrl(redirectUrl) {
		const rootUrl = window.location.origin;
		window.location.href = `${rootUrl}${redirectUrl}`;
	}

	getSiblingDependentQuestionIds() {
		return (this.data?.options || []).flatMap((option) => option.dependentQuestion || []);
	}

	updateDependentQuestions(selectedRadioButtonObject) {
		const newDependentQuestionIds = selectedRadioButtonObject?.dependentQuestion || [];

		if (this.data.beforeDependentQuestionChange) {
			// Pass all sibling dependent ids so only the selected option's field is shown.
			this.data.beforeDependentQuestionChange(newDependentQuestionIds, this.getSiblingDependentQuestionIds());
		}

		this._dependency = newDependentQuestionIds;
	}

	radioButtonClickHandler() {
		this.selectedRadio = this.radioButtonElements.find((radio) => radio.checked);
		const selectedRadioButtonObject = this?.data.options?.find((option) => option.id == this.selectedRadio.value);
		if (!selectedRadioButtonObject?.isOthers) {
			this.validate();
		}

		if (selectedRadioButtonObject) {
			this.value = {
				...selectedRadioButtonObject,
				fieldName: this?.data.fieldName,
				value: selectedRadioButtonObject[this?.data?.answerField || 'label']
			};
			this.updateDependentQuestions(selectedRadioButtonObject);
		}
		this.hideNotSelectedOthersInput();
		this.showSelectedOthersInput();
	}
	validate() {
		const formElementData = {
			element: this.radioButtonContainerElement,
			formElement: this.radioButtonElements,
			errorMessage: this.data.errorMessage,
			inputType: InputTypes.SELECTOR_RADIO,
			hasIcon: false
		};

		const otherInput = SelectorEngine.findOne(
			'.lta-radio-button-input-others:not(.u-hidden)',
			this.selectedRadio?.parentElement?.parentElement
		);
		const otherInputValue = otherInput?.value;

		if (otherInput && !otherInputValue) {
			formElementData.errorMessage = this.data.otherFieldErrorMessage;
		}

		const selectedRadioButtonObject = this?.data.options?.find((option) => option.id == this.selectedRadio?.value);
		const value = selectedRadioButtonObject?.isOthers ? otherInputValue : this.selectedRadio?.value;

		return this.data?.isOptional ? true : validFormElementValue(value, formElementData);
	}
}
export default RadioComponent;
